import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  Container,
  Row,
  Col,
  CardBody,
  Card,
} from "reactstrap";
import { get, post, put } from "helpers/api_helper";
import AddParentModule from "./AddParentModule";
import * as api from "../../../constants/api_constants";
import "../UserManagement.scss";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";

const TreeGrid = React.memo(
  ({ data, level = 0, expandedRows, toggleRow, handleSubModuleChanges }) => {
    const [addSubModuleToggle, setAddSubModuleToggle] = useState({});
    const [editModuleToggle, setEditModuleToggle] = useState({});
    const [moduleEditValues, setModuleEditValues] = useState({});
    const [originalModuleValues, setOriginalModuleValues] = useState({});

    const subModuleInputToggle = useCallback((moduleId) => {
      setAddSubModuleToggle((prev) => ({
        ...prev,
        [moduleId]: !prev[moduleId],
      }));
    }, []);

    const editModuleToggleHandler = useCallback((moduleId, module) => {
      setEditModuleToggle((prev) => ({
        ...prev,
        [moduleId]: !prev[moduleId],
      }));
      setModuleEditValues((prev) => ({
        ...prev,
        [moduleId]: module,
      }));
      setOriginalModuleValues((prev) => ({
        ...prev,
        [moduleId]: module,
      }));
    }, []);

    const handleInputChange = useCallback((moduleId, field, value) => {
      setModuleEditValues((prev) => ({
        ...prev,
        [moduleId]: {
          ...prev[moduleId],
          [field]: value,
        },
      }));
    }, []);

    const saveChanges = useCallback(
      (moduleId) => {
        handleSubModuleChanges(moduleEditValues[moduleId], "updateModule");
        setEditModuleToggle((prev) => ({
          ...prev,
          [moduleId]: false,
        }));
      },
      [handleSubModuleChanges, moduleEditValues]
    );

    const cancelChanges = useCallback(
      (moduleId) => {
        setModuleEditValues((prev) => ({
          ...prev,
          [moduleId]: originalModuleValues[moduleId],
        }));
        setEditModuleToggle((prev) => ({
          ...prev,
          [moduleId]: false,
        }));
      },
      [originalModuleValues]
    );

    const renderRow = useCallback(
      (item, level) => {
        const hasChildren = item.childModules && item.childModules.length > 0;
        const isToggled = addSubModuleToggle[item.moduleId] || false;
        const isEditing = editModuleToggle[item.moduleId] || false;

        return (
          <React.Fragment key={item.moduleId}>
            <tr className="UM_tree-tablerow">
              <td style={{ paddingLeft: `${(level + 1) * 20}px` }}>
                <div className="d-flex justify-content-start align-items-center">
                  {hasChildren ? (
                    <span
                      onClick={() => toggleRow(item.moduleId)}
                      style={{ marginRight: "10px" }}
                    >
                      {expandedRows[item.moduleId] ? (
                        <i className="ti-angle-down action-icon"></i>
                      ) : (
                        <i className="ti-angle-right action-icon"></i>
                      )}
                    </span>
                  ) : (
                    <span style={{ visibility: "hidden", marginRight: "10px" }}>
                      <i className="ti-angle-right action-icon"></i>
                    </span>
                  )}
                  {isEditing ? (
                    <input
                      type="text"
                      className="form-control"
                      value={moduleEditValues[item.moduleId]?.moduleName || ""}
                      onChange={(e) =>
                        handleInputChange(
                          item.moduleId,
                          "moduleName",
                          e.target.value
                        )
                      }
                    />
                  ) : (
                    item.moduleName
                  )}
                </div>
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="form-control"
                    value={moduleEditValues[item.moduleId]?.shortName || ""}
                    onChange={(e) =>
                      handleInputChange(
                        item.moduleId,
                        "shortName",
                        e.target.value
                      )
                    }
                  />
                ) : (
                  item.shortName
                )}
              </td>
              <td>{level === 0 ? "Module" : "Sub Module"}</td>
              <td>
                <div className="d-flex gap-3 align-items-center">
                  {isEditing ? (
                    <>
                      <i
                        className="fa-solid fa-floppy-disk action-icon text-success"
                        title="Save"
                        onClick={() => saveChanges(item.moduleId)}
                      ></i>
                      <i
                        className="fa-solid fa-xmark action-icon text-danger"
                        title="Cancel"
                        onClick={() => cancelChanges(item.moduleId)}
                      ></i>
                    </>
                  ) : (
                    <i
                      className="fa-regular fa-pen-to-square action-icon"
                      title="Edit"
                      onClick={() =>
                        editModuleToggleHandler(item.moduleId, item)
                      }
                    ></i>
                  )}
                  <div
                    onClick={() => subModuleInputToggle(item.moduleId)}
                    style={{ cursor: "pointer" }}
                  >
                    {level === 0 ? <i className="fa-solid fa-plus action-icon" title="Add"></i> : ""}
                  </div>
                </div>
              </td>
            </tr>
            {isToggled && level === 0 && (
              <tr style={{ paddingLeft: `${(level + 1) * 20}px` }}>
                <AddParentModule
                  handleModuleChanges={handleSubModuleChanges}
                  moduleId={item.moduleId}
                />
              </tr>
            )}
            {hasChildren && expandedRows[item.moduleId] && (
              <TreeGrid
                data={item.childModules}
                level={level + 1}
                expandedRows={expandedRows}
                toggleRow={toggleRow}
                handleSubModuleChanges={handleSubModuleChanges}
              />
            )}
          </React.Fragment>
        );
      },
      [
        addSubModuleToggle,
        editModuleToggle,
        moduleEditValues,
        expandedRows,
        toggleRow,
        handleSubModuleChanges,
        subModuleInputToggle,
        editModuleToggleHandler,
        handleInputChange,
        saveChanges,
        cancelChanges,
      ]
    );

    return <>{data.map((item) => renderRow(item, level))}</>;
  }
);

const Modules = () => {
  const [modulesList, setModuleData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState({});
  const rowsPerPage = 5;

  // Memoized calculations
  const totalRows = modulesList.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);

  // Fetch data with useCallback to prevent unnecessary rerenders
  const fetchData = useCallback(async () => {
    try {
      const ModuleData = await get(api.GET_ALL_MODULES);
      if (ModuleData?.modulePage?.content) {
        setModuleData(ModuleData.modulePage.content);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sorting handler with useCallback
  const handleSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  // Pagination handler with useCallback
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Row expansion toggle with useCallback
  const toggleRow = useCallback((moduleId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }));
  }, []);

  // Module changes handler with useCallback
  const handleModuleChanges = useCallback(
    async (values, action) => {
      try {
        switch (action) {
          case "addModule":
            await post(api.CREATE_MODULE, {
              moduleDTO: {
                moduleName: values.moduleName,
                shortName: values.moduleCode,
                parentModuleId: values.moduleParentId,
              },
            });
            break;
          case "updateModule":
            await put(api.UPDATE_MODULE_BY_ID(values.moduleId), {
              moduleDTO: {
                moduleName: values.moduleName,
                shortName: values.shortName,
                parentModuleId: values.parentModuleId,
              },
            });
            break;
          default:
            return;
        }
        fetchData();
      } catch (error) {
        console.error(`Error in ${action}:`, error);
      }
    },
    [fetchData]
  );

  // Memoized paginated data
  const paginatedModules = useMemo(
    () =>
      modulesList.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      ),
    [modulesList, currentPage, rowsPerPage]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid style={{ padding: "0px", gap: "10px" }}>
          <PageSubHeader
            heading={"Modules"}
            routePath={"Home"}
            pageName={"Modules"}
          />
          <Row className="mx-0 my-3 w-100 justify-content-between align-items-center">
            <Row className="py-3">
              <Col xs={12} lg={12}>
                <Card>
                  <CardBody>
                    <div className="table-responsive">
                      <Table className="table mb-0">
                        <thead>
                          <tr>
                            <th onClick={() => handleSort("moduleName")}>
                              Module Name
                            </th>
                            <th onClick={() => handleSort("modueleCode")}>
                              Short Name
                            </th>
                            <th>Module Type</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <AddParentModule
                              handleModuleChanges={handleModuleChanges}
                            />
                          </tr>
                          <TreeGrid
                            data={paginatedModules}
                            expandedRows={expandedRows}
                            toggleRow={toggleRow}
                            handleSubModuleChanges={handleModuleChanges}
                          />
                        </tbody>
                      </Table>
                      <Pagination className="d-flex flex-row justify-content-end align-items-center">
                        <PaginationItem disabled={currentPage === 1}>
                          <PaginationLink
                            first
                            onClick={() => handlePageChange(1)}
                          />
                        </PaginationItem>
                        <PaginationItem disabled={currentPage === 1}>
                          <PaginationLink
                            previous
                            onClick={() => handlePageChange(currentPage - 1)}
                          />
                        </PaginationItem>
                        {[...Array(totalPages)].map((_, i) => (
                          <PaginationItem
                            active={i + 1 === currentPage}
                            key={i}
                          >
                            <PaginationLink
                              onClick={() => handlePageChange(i + 1)}
                            >
                              {i + 1}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem disabled={currentPage === totalPages}>
                          <PaginationLink
                            next
                            onClick={() => handlePageChange(currentPage + 1)}
                          />
                        </PaginationItem>
                        <PaginationItem disabled={currentPage === totalPages}>
                          <PaginationLink
                            last
                            onClick={() => handlePageChange(totalPages)}
                          />
                        </PaginationItem>
                      </Pagination>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Modules;
